import { useEffect, useRef, useCallback } from 'react';

import { FlagsApi } from '@/api/api.flags';
import { FlagOrigin, SystemFlagType, IPersonFlag } from '@/models/flag';
import { ConfirmTypes, useToastStore } from '@/store';

import { useWantedPersonStatus } from './useWantedPersonStatus';

interface UseWantedPersonFlagSyncProps {
  personId?: string;
  onFlagChange?: () => void;
  disableAutoSync?: boolean; // New prop to disable auto-sync when viewing flags
}

/**
 * Hook to synchronize wanted person status with the WP system flag
 * Automatically assigns/removes the "WP" flag based on wanted person records
 */
export const useWantedPersonFlagSync = ({
  personId,
  onFlagChange,
  disableAutoSync = false,
}: UseWantedPersonFlagSyncProps) => {
  const { updateToast } = useToastStore();
  const { isWanted, loading: wantedLoading } = useWantedPersonStatus(personId);
  const lastWantedStatus = useRef<boolean | null>(null);
  const isProcessing = useRef(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialized = useRef(false);
  const initializationTimeout = useRef<NodeJS.Timeout | null>(null);

  // Memoize the flag change callback to prevent unnecessary re-runs
  const stableOnFlagChange = useCallback(() => {
    if (onFlagChange) {
      onFlagChange();
    }
  }, [onFlagChange]);

  const syncWantedFlag = useCallback(async () => {
    if (!personId || wantedLoading || isProcessing.current || disableAutoSync)
      return;

    // Don't sync until we've been initialized for at least 2 seconds
    if (!isInitialized.current) return;

    // Skip if status hasn't changed
    if (lastWantedStatus.current === isWanted) return;

    isProcessing.current = true;

    try {
      // Get current person flags to check if WP flag exists
      const flagsResponse = await FlagsApi.getPersonFlags(personId);
      const existingFlags = flagsResponse.data || [];
      const existingWPFlag = existingFlags.find(
        (flag: IPersonFlag) =>
          flag.flag.code === 'WP' &&
          flag.origin === FlagOrigin.SYSTEM &&
          flag.systemType === SystemFlagType.WANTED_PERSON &&
          !flag.isDeleted,
      );

      if (isWanted && !existingWPFlag) {
        // Person became wanted - assign WP system flag
        try {
          await FlagsApi.assignSystemFlag(personId, {
            systemType: SystemFlagType.WANTED_PERSON,
            reason: 'Automatically assigned due to wanted person record',
          });

          updateToast({
            open: true,
            message: 'Wanted Person flag automatically assigned',
            type: ConfirmTypes.SUCCESS,
          });

          // Use timeout to debounce flag change notifications
          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }
          syncTimeoutRef.current = setTimeout(() => {
            stableOnFlagChange();
          }, 100);
        } catch (error: any) {
          // Silent fail for automatic flag assignment
        }
      } else if (!isWanted && existingWPFlag) {
        // Additional safeguard: Only remove if we're very confident the person is not wanted
        // Wait a bit longer and double-check before removing
        try {
          // Double-check wanted status before removing
          const doubleCheckResponse = await FlagsApi.getPersonFlags(personId);
          const currentFlags = doubleCheckResponse.data || [];
          const stillHasWPFlag = currentFlags.find(
            (flag: IPersonFlag) =>
              flag.flag.code === 'WP' &&
              flag.origin === FlagOrigin.SYSTEM &&
              flag.systemType === SystemFlagType.WANTED_PERSON &&
              !flag.isDeleted,
          );

          if (stillHasWPFlag && !isWanted) {
            await FlagsApi.removePersonFlag(personId, stillHasWPFlag._id, {
              deleteReason: 'Automatically removed - no longer wanted',
            });

            updateToast({
              open: true,
              message: 'Wanted Person flag automatically removed',
              type: ConfirmTypes.SUCCESS,
            });

            // Use timeout to debounce flag change notifications
            if (syncTimeoutRef.current) {
              clearTimeout(syncTimeoutRef.current);
            }
            syncTimeoutRef.current = setTimeout(() => {
              stableOnFlagChange();
            }, 100);
          }
        } catch (error: any) {
          // Silent fail for automatic flag removal
        }
      }

      lastWantedStatus.current = isWanted;
    } catch (error: any) {
      // Silent fail for sync operation
    } finally {
      isProcessing.current = false;
    }
  }, [
    personId,
    isWanted,
    wantedLoading,
    updateToast,
    stableOnFlagChange,
    disableAutoSync,
  ]);

  // Initialize with delay to prevent immediate sync on component mount
  useEffect(() => {
    if (personId && !disableAutoSync) {
      // Wait 2 seconds before allowing sync operations
      initializationTimeout.current = setTimeout(() => {
        isInitialized.current = true;
      }, 2000);
    } else {
      isInitialized.current = false;
    }

    return () => {
      if (initializationTimeout.current) {
        clearTimeout(initializationTimeout.current);
      }
      isInitialized.current = false;
    };
  }, [personId, disableAutoSync]);

  useEffect(() => {
    // Only sync if not disabled and initialized
    if (!disableAutoSync && isInitialized.current) {
      // Debounce the sync operation to prevent rapid successive calls
      const debounceTimeout = setTimeout(() => {
        syncWantedFlag();
      }, 500); // Increased debounce time

      return () => {
        clearTimeout(debounceTimeout);
      };
    }
  }, [syncWantedFlag, disableAutoSync]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      if (initializationTimeout.current) {
        clearTimeout(initializationTimeout.current);
      }
    };
  }, []);

  return {
    isWanted,
    isLoading: wantedLoading || isProcessing.current,
  };
};
